import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { graphBuilderApi } from '../../services/api';
import './BuildGraph.css';
import './GraphPrompts.css';  // For menu indicator styles

function BuildGraphMenu({ 
  promptsStatus = {},  // { has_graph_customizations, has_document_overrides }
  onOpenPrompts,
  workspaceId,
  defaultProviderId,
}) {
  const { graphId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeDropdown, setActiveDropdown] = useState(null);
  // null | 'queued' | 'running' | 'success' | 'error'
  // ('loading' is mapped to 'running' in the notification rendering for back-compat styling)
  const [compileStatus, setCompileStatus] = useState(null);
  const [compileMessage, setCompileMessage] = useState('');
  const [compileJobId, setCompileJobId] = useState(null);
  const [compileMode, setCompileMode] = useState(null); // 'full' | 'fullSER' | 'sharded'
  const menuRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Menu structure
  const menuItems = [
    {
      label: 'Graph Home',
      key: 'graph-home',
      directLink: true,
      path: ''
    },
    {
      label: 'Build & Tune',
      key: 'build-tune',
      items: [
        // { label: 'Document Converter', path: 'document-converter' },
        { label: 'Expand Graph', path: 'expand-graph' },
        { label: 'Change nodes/edges', path: 'change-nodes-edges' },
        { label: 'Graph Stats', path: 'graph-stats' },
        { label: 'Query Expansion', path: 'query-expansion' },
        { label: 'Explainability', path: 'explainability' },
        { 
          label: 'Graph Prompts', 
          path: 'graph-prompts', 
          action: 'openPrompts',
          indicator: promptsStatus.has_graph_customizations 
            ? 'graph' 
            : (promptsStatus.has_document_overrides ? 'document' : null)
        }
      ]
    },
    {
      label: 'Train & Test',
      key: 'train-test',
      items: [
        { label: 'Review Feedback', path: 'review-feedback' },
        { label: 'Test Graph', path: 'test-graph' },
        { label: 'Test Cases', path: 'test-cases' }
      ]
    },
    {
      label: 'Connect & Stream',
      key: 'connect-stream',
      items: [
        { label: 'Endpoints', path: 'endpoints' },
        { label: 'Graph Links', path: 'graph-links' }
      ]
    },
    {
      label: 'Governance & Logs',
      key: 'governance-logs',
      items: [
        { label: 'Audit Logs', path: 'audit-logs' },
        { label: 'Dictionary', path: 'dictionary' }
      ]
    },
    {
      label: 'Deploy & Versions',
      key: 'deploy-versions',
      items: [
        { label: 'Compile Graph (Full)', action: 'compileFull', icon: '⚡' },
        { label: 'Compile Graph (Sharded)', action: 'compileSharded', icon: '📦' },
        { label: 'Compile with SER (Full)', action: 'compileFullSER', icon: '🧬' },
        { label: 'Copy to environment', path: 'copy-to-environment' }
      ]
    },
    {
      label: 'Process Maps',
      key: 'processes',
      directLink: true,
      path: 'processes',
      globalPath: true,
    }
  ];

  // Check if a menu item is active based on current route
  const isMenuItemActive = (categoryKey, itemPath) => {
    const currentPath = location.pathname;
    const expectedPath = `/user-home/graphs/build/${graphId}/${categoryKey}/${itemPath}`;
    return currentPath === expectedPath;
  };

  // Check if any item in a category is active
  const isCategoryActive = (categoryKey) => {
    return menuItems
      .find(cat => cat.key === categoryKey)
      ?.items.some(item => isMenuItemActive(categoryKey, item.path)) || false;
  };

  // Stop any in-flight polling and clear job state.
  const clearPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  // Format an ETA in seconds into "X min" / "Xs" for the notification line.
  const formatEta = (secs) => {
    if (secs == null || !isFinite(secs)) return null;
    if (secs < 60) return `${Math.max(0, Math.round(secs))}s`;
    const mins = Math.round(secs / 60);
    return `${mins} min`;
  };

  // Build the human-readable progress line shown in the notification.
  const renderProgressMessage = (mode, job) => {
    const phase = job.phase || 'starting';
    const pct = typeof job.percent === 'number' ? job.percent : 0;
    const eta = formatEta(job.eta_seconds);

    let phaseLabel = phase;
    if (phase === 'compile_arrays') phaseLabel = 'Compiling arrays';
    else if (phase === 'save_metadata') phaseLabel = 'Saving metadata';
    else if (phase === 'embed_synapses') phaseLabel = 'Embedding synapses';
    else if (phase === 'build_ser_receptors') phaseLabel = 'Building SER receptors';
    else if (phase === 'completed') phaseLabel = 'Finalizing';

    let detail = '';
    if (phase === 'embed_synapses' && job.batch_num != null && job.total_batches != null) {
      detail = ` (batch ${job.batch_num}/${job.total_batches})`;
    }
    const etaPart = eta ? ` — ETA ${eta}` : '';
    return `Compiling ${mode}: ${phaseLabel}${detail} — ${pct.toFixed(1)}%${etaPart}`;
  };

  // Begin polling a known job_id. Used by both fresh submits and refresh recovery.
  const startPollingJob = (jobId, mode) => {
    clearPolling();
    setCompileJobId(jobId);
    setCompileMode(mode);
    setCompileStatus('running');
    setCompileMessage(`Compiling ${mode}: starting…`);

    const tick = async () => {
      try {
        const job = await graphBuilderApi.getCompileJob(jobId);
        if (job.status === 'completed') {
          clearPolling();
          const stats = job.stats || {};
          const nodeCount = stats.nodes || stats.nodes_compiled || stats.node_count || 0;
          const synapseCount = stats.synapses || stats.synapses_total || stats.synapse_count || 0;
          const artifacts = job.artifacts || {};
          const serInfo = artifacts.ser_receptors ? ' + SER receptors' : '';
          setCompileStatus('success');
          setCompileMessage(
            `Compilation successful! Nodes: ${nodeCount}, Connections: ${synapseCount}${serInfo}`
          );
          setCompileJobId(null);
          setCompileMode(null);
          setTimeout(() => { setCompileStatus(null); setCompileMessage(''); }, 8000);
          return;
        }
        if (job.status === 'failed') {
          clearPolling();
          setCompileStatus('error');
          setCompileMessage(job.error || 'Compilation failed');
          setCompileJobId(null);
          setCompileMode(null);
          setTimeout(() => { setCompileStatus(null); setCompileMessage(''); }, 10000);
          return;
        }
        // queued | running
        setCompileMessage(renderProgressMessage(mode, job));
      } catch (err) {
        // 404 means the in-memory job vanished (e.g. graphbuilder restarted).
        if (err.response?.status === 404) {
          clearPolling();
          setCompileStatus('error');
          setCompileMessage(
            'Compile job is no longer tracked (server may have restarted). Please retry.'
          );
          setCompileJobId(null);
          setCompileMode(null);
          setTimeout(() => { setCompileStatus(null); setCompileMessage(''); }, 10000);
        }
        // Transient errors: leave the interval running and try again on next tick.
      }
    };

    tick();
    pollIntervalRef.current = setInterval(tick, 3000);
  };

  // Handle graph compilation: submit a background job, then poll for progress.
  const handleCompile = async (mode) => {
    if (compileStatus === 'queued' || compileStatus === 'running') {
      // Already running — ignore the click.
      return;
    }
    setActiveDropdown(null);
    setCompileStatus('queued');
    setCompileMessage(`Queueing ${mode} compile…`);

    const enableSer = mode === 'fullSER';
    const submitMode = mode === 'sharded' ? 'sharded' : 'full';

    try {
      const resp = await graphBuilderApi.compileGraphSubmit(graphId, {
        mode: submitMode,
        workspaceId,
        providerId: defaultProviderId,
        enableSer,
      });
      startPollingJob(resp.job_id, mode);
    } catch (err) {
      // 409 = a compile is already running for this graph; piggyback on it.
      if (err.response?.status === 409) {
        const detail = err.response.data?.detail || {};
        const existingJobId = detail.existing_job_id;
        if (existingJobId) {
          startPollingJob(existingJobId, detail.mode === 'sharded' ? 'sharded' : mode);
          return;
        }
      }
      console.error('Compile submit error:', err);
      setCompileStatus('error');
      setCompileMessage(
        err.response?.data?.detail?.message
          || (typeof err.response?.data?.detail === 'string' ? err.response.data.detail : null)
          || err.message
          || 'Failed to submit compile job'
      );
      setTimeout(() => { setCompileStatus(null); setCompileMessage(''); }, 10000);
    }
  };

  // Handle menu item click
  const handleMenuItemClick = (categoryKey, item) => {
    // Check if this item has a special action
    if (item.action === 'openPrompts' && onOpenPrompts) {
      onOpenPrompts();
      setActiveDropdown(null);
      return;
    }
    
    // Handle compile actions
    if (item.action === 'compileFull') {
      handleCompile('full');
      return;
    }
    if (item.action === 'compileSharded') {
      handleCompile('sharded');
      return;
    }
    if (item.action === 'compileFullSER') {
      handleCompile('fullSER');
      return;
    }
    
    navigate(`/user-home/graphs/build/${graphId}/${categoryKey}/${item.path}`);
    setActiveDropdown(null);
  };

  // Handle dropdown toggle
  const handleDropdownToggle = (key, event) => {
    if (event.type === 'mouseenter') {
      // Desktop: open on hover
      setActiveDropdown(key);
    } else if (event.type === 'click') {
      // Mobile/touch: toggle on click
      setActiveDropdown(activeDropdown === key ? null : key);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    if (activeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeDropdown]);

  // Close dropdown on route change
  useEffect(() => {
    setActiveDropdown(null);
  }, [location.pathname]);

  // On mount / graphId change: if a compile is already running on the server
  // (e.g. user refreshed the page mid-compile), re-attach to it instead of
  // showing a stale UI. Also: cleanup the poll interval when unmounting.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!graphId) return;
      try {
        const active = await graphBuilderApi.getActiveCompileJob(graphId);
        if (cancelled) return;
        if (active?.active && active.job_id) {
          const recoveredMode = active.mode === 'sharded' ? 'sharded' : 'full';
          startPollingJob(active.job_id, recoveredMode);
        }
      } catch (_) {
        // Best-effort recovery; ignore errors.
      }
    })();
    return () => {
      cancelled = true;
      clearPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphId]);

  // Handle direct link navigation (for Graph Home, Process Maps, etc.)
  const handleDirectLinkClick = (category) => {
    if (category.path === '') {
      navigate(`/user-home/graphs/build/${graphId}`);
    } else if (category.globalPath) {
      navigate(`/user-home/process-maps?graph_id=${graphId}`);
    } else {
      navigate(`/user-home/graphs/build/${graphId}/${category.path}`);
    }
  };

  // Check if direct link is active
  const isDirectLinkActive = (category) => {
    if (!category.directLink) return false;
    if (category.path === '') {
      return location.pathname === `/user-home/graphs/build/${graphId}`;
    }
    if (category.globalPath) {
      return location.pathname.includes('/process-maps') ||
             location.pathname.includes(`/graphs/build/${graphId}/${category.path}`);
    }
    return location.pathname.includes(`/graphs/build/${graphId}/${category.path}`);
  };

  return (
    <nav className="build-graph-menu" ref={menuRef}>
      {/* Compile Status Notification */}
      {compileStatus && (
        <div className={`compile-notification compile-notification--${
          compileStatus === 'queued' || compileStatus === 'running' ? 'loading' : compileStatus
        }`}>
          {(compileStatus === 'queued' || compileStatus === 'running') && (
            <span className="compile-spinner"></span>
          )}
          {compileStatus === 'success' && <span className="compile-icon">✓</span>}
          {compileStatus === 'error' && <span className="compile-icon">✕</span>}
          <span className="compile-message">{compileMessage}</span>
          {compileStatus !== 'queued' && compileStatus !== 'running' && (
            <button 
              className="compile-dismiss"
              onClick={() => { setCompileStatus(null); setCompileMessage(''); }}
            >
              ×
            </button>
          )}
        </div>
      )}
      <div className="build-graph-menu__container">
        {menuItems.map((category) => (
          <div
            key={category.key}
            className={`build-graph-menu__item ${
              category.directLink 
                ? (isDirectLinkActive(category) ? 'active' : '')
                : (isCategoryActive(category.key) ? 'active' : '')
            }`}
            onMouseEnter={(e) => !category.directLink && handleDropdownToggle(category.key, e)}
            onMouseLeave={() => {
              if (!category.directLink) {
                // Delay closing to allow mouse to move to dropdown
                setTimeout(() => {
                  if (activeDropdown === category.key) {
                    setActiveDropdown(null);
                  }
                }, 150);
              }
            }}
          >
            <button
              className="build-graph-menu__button"
              onClick={(e) => category.directLink 
                ? handleDirectLinkClick(category) 
                : handleDropdownToggle(category.key, e)
              }
            >
              {category.label}
              {!category.directLink && (
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className={`build-graph-menu__arrow ${
                    activeDropdown === category.key ? 'open' : ''
                  }`}
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              )}
            </button>
            {!category.directLink && activeDropdown === category.key && (
              <div 
                className="build-graph-menu__dropdown"
                onMouseEnter={() => {
                  // Keep dropdown open when hovering over it
                  if (activeDropdown !== category.key) {
                    setActiveDropdown(category.key);
                  }
                }}
                onMouseLeave={() => {
                  setActiveDropdown(null);
                }}
              >
                {category.items.map((item) => {
                  const isCompileAction = item.action === 'compileFull'
                    || item.action === 'compileSharded'
                    || item.action === 'compileFullSER';
                  const compileBusy = compileStatus === 'queued' || compileStatus === 'running';
                  const itemDisabled = isCompileAction && compileBusy;
                  return (
                    <button
                      key={item.path || item.action}
                      className={`build-graph-menu__dropdown-item ${
                        item.path && isMenuItemActive(category.key, item.path) ? 'active' : ''
                      } ${item.action ? 'action-item' : ''}`}
                      onClick={() => handleMenuItemClick(category.key, item)}
                      disabled={itemDisabled}
                      title={itemDisabled ? 'A compile job is already in progress' : undefined}
                    >
                      {item.icon && <span className="menu-item-icon">{item.icon}</span>}
                      {item.label}
                      {itemDisabled && <span style={{ marginLeft: 6, opacity: 0.7 }}>(in progress)</span>}
                      {item.indicator && (
                        <span 
                          className={`menu-prompt-indicator ${
                            item.indicator === 'graph' ? 'graph-customized' : 'document-customized'
                          }`}
                          title={item.indicator === 'graph' ? 'Graph prompts customized' : 'Document prompts customized'}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </nav>
  );
}

export default BuildGraphMenu;

